import { InlineAlertType } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import { AutopayConfirmationError } from 'features/payOverTime/helpers/PayOverTime.helpers';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusTrackAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const ApplicationSuccess = () => {
  const shouldNavigateHomeRef = useRef(false);
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'applicationSuccess',
  });

  const dismissToTab = useDismissToRootTab();
  const { refreshTiles, fetching } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BORROW,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_SAVINGS,
  ]);
  const { amount, firstPaymentAmount, firstPaymentDate, autopayError } =
    useLocalSearchParams<{
      amount: string;
      firstPaymentAmount: string;
      firstPaymentDate: string;
      autopayError?: string;
    }>();
  const autopayErrorType = Object.values(AutopayConfirmationError).find(
    (value) => value === autopayError,
  );
  const hasAutopayError = Boolean(autopayErrorType);
  const autopayErrorCopyKey =
    autopayErrorType === AutopayConfirmationError.UnconfirmedOff
      ? 'unconfirmedOff'
      : 'unconfirmedOn';

  const goHome = useCallback(() => {
    shouldNavigateHomeRef.current = true;
    refreshTiles();
  }, [refreshTiles]);

  useEffect(() => {
    if (shouldNavigateHomeRef.current && !fetching) {
      shouldNavigateHomeRef.current = false;
      dismissToTab(Tabs.HOME);
    }
  }, [fetching, dismissToTab]);

  useViewDidFocusTrackAnalyticsEvent({
    name: 'Pay Over Time Application Approved',
    customAttributes: {
      category: 'pay_over_time',
      source_name: 'pay_over_time_application_approved',
    },
  });

  useBackHandler(goHome);

  const bulletItems = [
    translatedText('bulletOne', { amount }),
    translatedText('bulletTwo', {
      amount: firstPaymentAmount,
      date: firstPaymentDate,
    }),
  ];

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          gestureEnabled: false,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} empty primaryIcon={null} />
          ),
        }}
      />
      <SuccessScreen
        screenAnalyticsEventKey="/pay-over-time/application-success"
        primaryButton={{
          label: translatedText('button'),
          onPress: goHome,
        }}
        titleText={translatedText('title')}
        visual="DISCOBALL_A"
        content={
          <View style={styles.listContainer}>
            <ContentList contentList={bulletItems} />
            {hasAutopayError ? (
              <View style={styles.alertContainer}>
                <InlineAlert
                  alertType={InlineAlertType.Error}
                  titleText={translatedText(
                    `autopayError.${autopayErrorCopyKey}.title`,
                  )}
                  bodyText={translatedText(
                    `autopayError.${autopayErrorCopyKey}.body`,
                  )}
                  isImageVisible={false}
                />
              </View>
            ) : null}
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  alertContainer: {
    marginTop: theme.spacing.m,
  },
  listContainer: {
    alignSelf: 'stretch',
    paddingHorizontal: theme.spacing.m,
  },
  screen: {
    flex: 1,
  },
});

export default ApplicationSuccess;
